import { supabase } from './supabase';
import { format } from 'date-fns';
import { cobrancaService } from './cobrancaService';

/**
 * Configuração da API do WhatsApp
 */
interface WhatsAppConfig {
  serverUrl: string;
  apikey: string;
  instanceName: string;
}

/**
 * Tipos de mensagens que podem ser enviadas
 */
export enum WhatsAppMessageType {
  TEXT = 'text',
  DOCUMENT = 'document',
  IMAGE = 'image',
  AUDIO = 'audio',
  VIDEO = 'video',
  STICKER = 'sticker',
  CONTACT = 'contact',
  LOCATION = 'location',
  TEMPLATE = 'template',
  BUTTON = 'button',
  LIST = 'list',
}

/**
 * Opções para envio de mensagem
 */
interface SendMessageOptions {
  delay?: number;
  presence?: 'unavailable' | 'available' | 'composing' | 'recording' | 'paused';
}

/**
 * Parâmetros para envio de mensagem de texto
 */
interface SendTextParams {
  telefone: string;
  mensagem: string;
  options?: SendMessageOptions;
  codempresa?: number; // ID da empresa para buscar a configuração correta
  registrarCobranca?: boolean; // Se deve registrar a cobrança na API
  dadosCobranca?: {
    codfunc?: string; // Código do funcionário
    codcli?: string; // Código do cliente
    titulos?: any[]; // Lista de títulos para registrar
  };
}

/**
 * Parâmetros para envio de documento
 */
interface SendDocumentParams {
  telefone: string;
  url: string;
  fileName?: string;
  caption?: string;
  options?: SendMessageOptions;
  codempresa?: number; // ID da empresa para buscar a configuração correta
  registrarCobranca?: boolean; // Se deve registrar a cobrança na API
  dadosCobranca?: SendTextParams['dadosCobranca']; // Dados da cobrança a ser registrada
}

/**
 * Parâmetros para envio de imagem
 */
interface SendImageParams {
  telefone: string;
  url: string;
  caption?: string;
  options?: SendMessageOptions;
  codempresa?: number; // ID da empresa para buscar a configuração correta
  registrarCobranca?: boolean; // Se deve registrar a cobrança na API
  dadosCobranca?: SendTextParams['dadosCobranca']; // Dados da cobrança a ser registrada
}

/**
 * Parâmetros para envio de modelo de mensagem
 */
interface SendTemplateParams {
  telefone: string;
  modeloId: string;
  params: Record<string, string>;
  options?: SendMessageOptions;
}

/**
 * Serviço para integração com WhatsApp
 */
export const whatsappService = {
  /**
   * Verifica se um número de telefone é um número de WhatsApp válido
   * @param telefone Número de telefone a ser verificado
   * @param codempresa Código da empresa para buscar a configuração correta
   * @returns Promise<boolean> Verdadeiro se o número for válido, falso caso contrário
   */
  async verificarNumeroWhatsApp(telefone: string, codempresa: number): Promise<boolean> {
    try {
      // Validar telefone
      if (!telefone) {
        throw new Error('Telefone não informado');
      }
      
      // Formatar telefone (remover caracteres especiais e adicionar código do país se necessário)
      const telefoneFormatado = this.formatarTelefone(telefone);
      if (!this.validarTelefone(telefoneFormatado)) {
        console.log(`Telefone inválido: ${telefoneFormatado}`);
        return false;
      }
      
      console.log(`Verificando se o número ${telefoneFormatado} é um WhatsApp válido`);
      
      // Verificar se o código da empresa está disponível
      if (!codempresa) {
        throw new Error('Código da empresa não disponível');
      }
      
      // Buscar dados da instância usando a função centralizada
      const { config, instanceName } = await this.buscarDadosInstancia(codempresa);
      
      // Construir a URL para verificação do número
      let url = config.serverUrl || '';
      
      // Remover a barra final da URL se existir para evitar barras duplas
      if (url.endsWith('/')) {
        url = url.slice(0, -1);
      }
      
      // Sempre usar HTTPS para garantir compatibilidade com ambientes seguros
      if (url.startsWith('http://')) {
        // Substituir http:// por https://
        url = url.replace('http://', 'https://');
        console.log('URL ajustada para HTTPS:', url);
      }
      
      // Se a URL não tiver protocolo, adicionar https://
      if (!url.startsWith('http')) {
        url = `https://${url}`;
        console.log('Adicionado protocolo HTTPS à URL:', url);
      }
      
      // Buscar a instância diretamente na tabela infraestrutura_chatbot
      console.log(`Buscando instância na tabela infraestrutura_chatbot para empresa ${codempresa}`);
      
      const { data: chatbotData, error: chatbotError } = await supabase
        .from('infraestrutura_chatbot')
        .select('*')
        .eq('tipo_integracao', 'smartcobranca')
        .eq('codempresa', codempresa)
        .limit(1);
      
      if (chatbotError) {
        console.error('Erro ao buscar configuração do chatbot:', chatbotError);
        throw new Error(`Erro ao buscar configuração de chatbot para empresa ${codempresa}`);
      }
      
      // Verificar se encontrou configuração
      if (!chatbotData || chatbotData.length === 0) {
        console.error(`Configuração não encontrada para empresa ${codempresa}`);
        throw new Error(`Configuração de chatbot não encontrada para empresa ${codempresa}`);
      }
      
      const configChatbot = chatbotData[0];
      
      if (!configChatbot.instance_name) {
        throw new Error(`Instância não configurada para a empresa ${codempresa}. Verifique a tabela infraestrutura_chatbot.`);
      }
      
      console.log('Usando instância:', configChatbot.instance_name);
      
      // Endpoint para verificar se o número é um WhatsApp válido
      url = `${url}/chat/whatsappNumbers/${instanceName}`;
      
      console.log('URL para verificação:', url);
      
      // Preparar os dados para a requisição
      const requestData = {
        numbers: [telefoneFormatado.replace('@s.whatsapp.net', '')]
      };
      
      console.log('Enviando requisição para verificar número:', url);
      console.log('Dados:', requestData);
      
      // Fazer a requisição
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': config.apikey
        },
        body: JSON.stringify(requestData)
      });
      
      if (!response.ok) {
        console.error(`Erro HTTP: ${response.status} ${response.statusText}`);
        return false;
      }
      
      const responseData = await response.json();
      console.log('Resposta da verificação de número:', responseData);
      
      // Verificar se o número é válido
      // A resposta agora é um array de objetos, cada um com a propriedade "exists"
      return Array.isArray(responseData) && 
             responseData.length > 0 && 
             responseData[0].exists === true;
    } catch (error) {
      console.error('Erro ao verificar número de WhatsApp:', error);
      return false;
    }
  },
  
  /**
   * Busca a configuração do WhatsApp para um tipo de integração específico
   * @param tipoIntegracao Tipo de integração (ex: "smartcobranca")
   */
  async getConfig(tipoIntegracao: string): Promise<WhatsAppConfig | null> {
    try {
      // Buscar configuração da infraestrutura_chatbot
      const { data, error } = await supabase
        .from('infraestrutura_chatbot')
        .select('*')
        .eq('tipo_integracao', tipoIntegracao)
        .limit(1);

      if (error) {
        console.error('Erro ao buscar infraestrutura do chatbot:', error);
        return null;
      }

      if (!data || data.length === 0) {
        console.error(`Configuração não encontrada para tipo de integração ${tipoIntegracao}`);
        return null;
      }

      // Extrair configuração da API
      const config: WhatsAppConfig = {
        serverUrl: data[0].api_whatsapp?.serverUrl || '',
        apikey: data[0].api_whatsapp?.apikey || '',
        instanceName: data[0].api_whatsapp?.instanceName || ''
      };

      return config;
    } catch (err) {
      console.error('Erro ao buscar configuração do WhatsApp:', err);
      return null;
    }
  },
  
  /**
   * Formata o número de telefone para o formato correto
   * @param telefone Número de telefone (com ou sem formatação)
   */
  formatarTelefone(telefone: string): string {
    // Remover caracteres não numéricos
    const numeroLimpo = telefone.replace(/\D/g, '');
    
    // Verificar se já começa com 55 (código do Brasil)
    if (!numeroLimpo.startsWith('55')) {
      return `55${numeroLimpo}`;
    }
    
    return numeroLimpo;
  },

  /**
   * Valida se o número de telefone está no formato correto
   * @param telefone Número de telefone formatado
   */
  validarTelefone(telefone: string): boolean {
    // Deve ter o formato 55DDNNNNNNNNN (13 ou 14 dígitos)
    return /^55\d{10,11}$/.test(telefone);
  },

  /**
   * Busca os dados da instância do WhatsApp na tabela infraestrutura_chatbot
   * @param codempresa Código da empresa
   * @returns Dados da instância do WhatsApp
   */
  async buscarDadosInstancia(codempresa: number) {
    try {
      console.log(`Buscando dados da instância para empresa ${codempresa}`);
      
      // Buscar configuração na tabela infraestrutura_chatbot
      const { data, error } = await supabase
        .from('infraestrutura_chatbot')
        .select('*')
        .eq('tipo_integracao', 'smartcobranca')
        .eq('codempresa', codempresa)
        .limit(1);
      
      if (error) {
        console.error('Erro ao buscar configuração do chatbot:', error);
        throw new Error(`Erro ao buscar configuração de chatbot para empresa ${codempresa}`);
      }
      
      // Verificar se encontrou configuração
      if (!data || data.length === 0) {
        console.error(`Configuração não encontrada para empresa ${codempresa}`);
        throw new Error(`Configuração de chatbot não encontrada para empresa ${codempresa}`);
      }
      
      const configChatbot = data[0];
      
      // Verificar se tem os dados necessários
      if (!configChatbot.api_whatsapp) {
        throw new Error(`Configuração de API WhatsApp não encontrada para empresa ${codempresa}`);
      }
      
      if (!configChatbot.instance_name) {
        throw new Error(`Instância não configurada para a empresa ${codempresa}. Verifique a tabela infraestrutura_chatbot.`);
      }
      
      return {
        configChatbot,
        config: configChatbot.api_whatsapp,
        instanceName: configChatbot.instance_name
      };
    } catch (error) {
      console.error('Erro ao buscar dados da instância:', error);
      throw error;
    }
  },

  /**
   * Envia uma mensagem de texto via WhatsApp
   * @param params Parâmetros para envio de mensagem de texto
   * @param tipoIntegracao Tipo de integração (ex: "smartcobranca")
   */
  async enviarTexto(params: SendTextParams, tipoIntegracao: string = 'evolutionapi'): Promise<boolean> {
    try {
      console.log('Iniciando envio de mensagem via WhatsApp...');
      
      // Verificar se codempresa foi fornecido
      if (!params.codempresa) {
        console.error('codempresa não fornecido');
        throw new Error('É necessário fornecer o código da empresa para enviar mensagens');
      }
      
      console.log(`Usando codempresa fornecido: ${params.codempresa}`);
      
      // Buscar dados da instância usando a função centralizada
      const { configChatbot, config } = await this.buscarDadosInstancia(params.codempresa);
      
      console.log('Configuração encontrada para a empresa:', configChatbot);

      // Garantir que o telefone não seja undefined
      if (!params.telefone) {
        console.error('Número de telefone não fornecido');
        throw new Error('Número de telefone não fornecido');
      }

      // Formatar o número de telefone
      const telefoneFormatado = params.telefone.includes('@s.whatsapp.net')
        ? params.telefone
        : `${this.formatarTelefone(params.telefone)}@s.whatsapp.net`;

      console.log('Telefone formatado:', telefoneFormatado);

      // Construir a URL para envio da mensagem usando os dados da configuração
      let url = config.serverUrl || '';
      
      // Remover a barra final da URL se existir para evitar barras duplas
      if (url.endsWith('/')) {
        url = url.slice(0, -1);
      }
      
      // Sempre usar HTTPS para garantir compatibilidade com ambientes seguros
      if (url.startsWith('http://')) {
        // Substituir http:// por https://
        url = url.replace('http://', 'https://');
        console.log('URL ajustada para HTTPS:', url);
      }
      
      // Se a URL não tiver protocolo, adicionar https://
      if (!url.startsWith('http')) {
        url = `https://${url}`;
        console.log('Adicionado protocolo HTTPS à URL:', url);
      }
      
      // Verificar se a instância está configurada
      if (!configChatbot.instance_name) {
        throw new Error('Instância não configurada para a API Evolution');
      }
      
      // Usar a instância configurada no banco de dados
      url = `${url}/message/sendText/${configChatbot.instance_name}`;
      console.log('URL de envio:', url);

      // Preparar a mensagem
      const mensagemTruncada = params.mensagem.length > 100 
        ? params.mensagem.substring(0, 100) + '...' 
        : params.mensagem;
      console.log('Mensagem a ser enviada:', mensagemTruncada);

      // Preparar os dados para envio conforme documentação da Evolution API
      const dadosEnvio = {
        number: telefoneFormatado,
        text: params.mensagem,
        delay: params.options?.delay || 1200
      };

      console.log('Dados completos para envio:', dadosEnvio);
      console.log('Iniciando requisição para a API...');

      // Enviar a mensagem
      console.log('Enviando requisição para:', url);
      // Removido log de apikey por segurança
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': config.apikey || ''
        },
        body: JSON.stringify(dadosEnvio)
      });

      console.log('Status da resposta:', response.status);
      console.log('Status text:', response.statusText);

      if (!response.ok) {
        let errorMessage = `Erro HTTP: ${response.status} ${response.statusText}`;
        try {
          const errorData = await response.json();
          console.error('Dados do erro:', JSON.stringify(errorData, null, 2));
          errorMessage = `Erro ao enviar mensagem: ${errorData.message || response.statusText}`;
        } catch (jsonError) {
          console.error('Erro ao parsear resposta de erro:', jsonError);
          const errorText = await response.text();
          console.error('Texto da resposta de erro:', errorText);
          errorMessage = `Erro ao enviar mensagem: ${errorText || response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      try {
        const responseData = await response.json();
        console.log('Resposta completa:', JSON.stringify(responseData, null, 2));
        
        // Se a opção de registrar cobrança estiver ativada e tivermos os dados necessários
        if (params.registrarCobranca && params.dadosCobranca && params.codempresa) {
          console.log('Iniciando registro de cobrança na API...');
          console.log('Dados para registro:', {
            telefone: params.telefone,
            codempresa: params.codempresa,
            dadosCobranca: JSON.stringify(params.dadosCobranca)
          });
          
          try {
            await this.registrarCobrancaAPI(params.telefone, params.dadosCobranca, params.codempresa);
            console.log('Cobrança registrada com sucesso!');
          } catch (registroErr) {
            console.error('Erro ao registrar cobrança na API:', registroErr);
            // Não lançamos o erro para não interromper o fluxo principal
          }
        } else {
          console.log('Não foi possível registrar a cobrança. Dados ausentes:', {
            registrarCobranca: params.registrarCobranca,
            temDadosCobranca: !!params.dadosCobranca,
            temCodEmpresa: !!params.codempresa
          });
        }
        
        return true;
      } catch (jsonError) {
        console.error('Erro ao parsear resposta de sucesso:', jsonError);
        const responseText = await response.text();
        console.log('Texto da resposta:', responseText);
        return true; // Consideramos sucesso mesmo se não conseguirmos parsear o JSON
      }
    } catch (err: any) {
      console.error('Erro ao enviar mensagem de texto:', err.message);
      console.error('Stack trace:', err.stack);
      throw err;
    }
  },

  /**
   * Envia um documento via WhatsApp
   * @param params Parâmetros para envio de documento
   * @param tipoIntegracao Tipo de integração (ex: "smartcobranca")
   */
  async enviarDocumento(params: SendDocumentParams, tipoIntegracao: string = 'smartcobranca'): Promise<boolean> {
    try {
      // Verificar se os parâmetros obrigatórios estão presentes
      if (!params.telefone || !params.url) {
        throw new Error('Telefone e URL do documento são obrigatórios para envio de documento');
      }
      
      // Verificar se o código da empresa está disponível
      if (!params.codempresa) {
        throw new Error('Código da empresa não disponível');
      }
      
      // Buscar dados da instância usando a função centralizada
      const { config, instanceName } = await this.buscarDadosInstancia(params.codempresa);

      // Formatar o número de telefone
      const telefoneFormatado = this.formatarTelefone(params.telefone);
      
      // Validar o número de telefone
      if (!this.validarTelefone(telefoneFormatado)) {
        throw new Error('Número de telefone inválido. Deve estar no formato 55DDNNNNNNNNN');
      }

      // URL para envio de documento
      const url = `${config.serverUrl}/message/sendMedia/${instanceName}`;
      
      // Opções padrão se não forem fornecidas
      const options = params.options || {
        delay: 1200,
        presence: "composing"
      };
      
      // Dados para envio de documento
      const data = {
        number: `${telefoneFormatado}@s.whatsapp.net`,
        options,
        mediaMessage: {
          mediatype: "document",
          media: params.url,
          fileName: params.fileName || "documento.pdf",
          caption: params.caption || ""
        }
      };

      console.log('Enviando documento para:', telefoneFormatado);
      console.log('URL:', url);

      // Enviar documento
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': config.apikey
        },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(`Erro ao enviar documento: ${errorData.message || response.statusText}`);
      }

      const responseData = await response.json();
      console.log('Resposta:', responseData);

      return true;
    } catch (err: any) {
      console.error('Erro ao enviar documento:', err);
      throw err;
    }
  },

  /**
   * Envia uma imagem via WhatsApp
   * @param params Parâmetros para envio de imagem
   * @param tipoIntegracao Tipo de integração (ex: "smartcobranca")
   */
  async enviarImagem(params: SendImageParams, tipoIntegracao: string = 'smartcobranca'): Promise<boolean> {
    try {
      // Verificar se os parâmetros obrigatórios estão presentes
      if (!params.telefone || !params.url) {
        throw new Error('Telefone e URL da imagem são obrigatórios para envio de imagem');
      }
      
      // Verificar se o código da empresa está disponível
      if (!params.codempresa) {
        throw new Error('Código da empresa não disponível');
      }
      
      // Buscar dados da instância usando a função centralizada
      const { config, instanceName } = await this.buscarDadosInstancia(params.codempresa);

      // Formatar o número de telefone
      const telefoneFormatado = this.formatarTelefone(params.telefone);
      
      // Validar o número de telefone
      if (!this.validarTelefone(telefoneFormatado)) {
        throw new Error('Número de telefone inválido. Deve estar no formato 55DDNNNNNNNNN');
      }

      // URL para envio de imagem
      const url = `${config.serverUrl}/message/sendMedia/${instanceName}`;
      
      // Opções padrão se não forem fornecidas
      const options = params.options || {
        delay: 1200,
        presence: "composing"
      };
      
      // Dados para envio de imagem
      const data = {
        number: `${telefoneFormatado}@s.whatsapp.net`,
        options,
        mediaMessage: {
          mediatype: "image",
          media: params.url,
          caption: params.caption || ""
        }
      };

      console.log('Enviando imagem para:', telefoneFormatado);
      console.log('URL:', url);

      // Enviar imagem
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': config.apikey
        },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(`Erro ao enviar imagem: ${errorData.message || response.statusText}`);
      }

      const responseData = await response.json();
      console.log('Resposta:', responseData);

      return true;
    } catch (err: any) {
      console.error('Erro ao enviar imagem:', err);
      throw err;
    }
  },

  /**
   * Envia uma mensagem usando um modelo pré-definido
   * @param params Parâmetros para envio de modelo de mensagem
   * @param tipoIntegracao Tipo de integração (ex: "smartcobranca")
   */
  async enviarModelo(params: SendTemplateParams, tipoIntegracao: string = 'smartcobranca'): Promise<boolean> {
    try {
      // Buscar modelo de mensagem
      const { data: modeloData, error: modeloError } = await supabase
        .from('modelomensagem')
        .select('*')
        .eq('id', params.modeloId)
        .limit(1);

      if (modeloError) {
        console.error('Erro ao buscar modelo de mensagem:', modeloError);
        throw new Error(`Erro ao buscar modelo de mensagem: ${modeloError.message}`);
      }

      if (!modeloData || modeloData.length === 0) {
        throw new Error(`Modelo de mensagem não encontrado: ${params.modeloId}`);
      }

      // Buscar configuração do WhatsApp
      const config = await this.getConfig(tipoIntegracao);
      if (!config) {
        throw new Error(`Configuração do WhatsApp para ${tipoIntegracao} não encontrada`);
      }

      // Formatar o número de telefone
      const telefoneFormatado = this.formatarTelefone(params.telefone);
      
      // Validar o número de telefone
      if (!this.validarTelefone(telefoneFormatado)) {
        throw new Error('Número de telefone inválido. Deve estar no formato 55DDNNNNNNNNN');
      }

      // Substituir parâmetros no modelo
      let mensagem = modeloData[0].conteudo;
      for (const [key, value] of Object.entries(params.params)) {
        mensagem = mensagem.replace(new RegExp(`{{${key}}}`, 'g'), value);
      }

      // Enviar mensagem de texto com o modelo processado
      return await this.enviarTexto({
        telefone: params.telefone,
        mensagem,
        options: params.options
      }, tipoIntegracao);
    } catch (err: any) {
      console.error('Erro ao enviar modelo de mensagem:', err);
      throw err;
    }
  },

  /**
   * Busca um modelo de mensagem no banco de dados
   * @param tipo Tipo do modelo (não usado, mantido para compatibilidade)
   * @param codempresa Código da empresa (obrigatório)
   */
  /**
   * Registra uma cobrança na API após o envio da mensagem de WhatsApp
   * @param telefone Número de telefone para o qual a mensagem foi enviada
   * @param dadosCobranca Dados da cobrança a ser registrada
   * @param codempresa Código da empresa
   */
  async registrarCobrancaAPI(telefone: string, dadosCobranca: SendTextParams['dadosCobranca'], codempresa: number): Promise<void> {
    if (!dadosCobranca || !dadosCobranca.codcli || !dadosCobranca.codfunc) {
      throw new Error('Dados insuficientes para registrar cobrança');
    }

    try {
      console.log('Iniciando registro de cobrança via proxy...');
      
      // Formatar o telefone para o formato esperado pela API
      const telefoneFormatado = this.formatarTelefone(telefone);
      const telcob = telefoneFormatado.replace('@s.whatsapp.net', '');
      
      // 1. Registrar a cobrança principal
      const dadosPrincipais = {
        codfunc: dadosCobranca.codfunc,
        codcli: dadosCobranca.codcli,
        telcob: telcob, // Usa o telefone fornecido pelo usuário
        obs1: '',
        statuscob: 'WHATSAPP'
      };

      console.log('Registrando cobrança principal:', dadosPrincipais);
      const resultado = await cobrancaService.registrarCobranca(dadosPrincipais, codempresa);
      console.log('Cobrança registrada com sucesso:', resultado);

      // 2. Se houver títulos, registrar os itens da cobrança
      if (dadosCobranca.titulos && dadosCobranca.titulos.length > 0) {
        console.log('Processando', dadosCobranca.titulos.length, 'títulos para registro...');
        
        const itensCobranca = dadosCobranca.titulos.map(titulo => {
          // Formatar a data para DD/MM/YYYY
          let dataVencimento = titulo.D_DTVENC || '';
          
          // Verificar se a data está no formato ISO
          if (dataVencimento && dataVencimento.includes('T')) {
            try {
              const data = new Date(dataVencimento);
              dataVencimento = format(data, 'dd/MM/yyyy');
              console.log(`Data original: ${titulo.D_DTVENC} => Data formatada: ${dataVencimento}`);
            } catch (error) {
              console.error('Erro ao formatar data:', error);
            }
          }
          
          return {
            codcli: dadosCobranca.codcli || '',
            codfilial: titulo.I_CODFILIAL || '',
            numtransvenda: titulo.I_NUMTRANSVENDA || '',
            duplic: titulo.I_DUPLIC || '',
            prest: titulo.S_PREST || '',
            dtvenc: dataVencimento,
            valor: titulo.F_VALOR || '',
            codcob: titulo.S_CODCOB || '',
            valorprev: titulo.F_VALOR || '',
            statuscob: 'WHATSAPP'
          };
        });

        console.log('Registrando itens da cobrança...');
        const resultadoItens = await cobrancaService.registrarItensCobranca(itensCobranca, codempresa);
        console.log('Itens registrados com sucesso:', resultadoItens);
      }

      console.log('Cobrança registrada com sucesso na API');
    } catch (error) {
      console.error('Erro ao registrar cobrança:', error);
      throw error;
    }
  },

  /**
   * Busca um modelo de mensagem no banco de dados
   * @param tipo Tipo do modelo (não usado, mantido para compatibilidade)
   * @param codempresa Código da empresa (obrigatório)
   */
  async buscarModelo(tipo: string, codempresa: number): Promise<string | null> {
    try {
      if (!codempresa) {
        console.error('Código da empresa não fornecido');
        return null;
      }
      
      console.log(`Buscando modelo de mensagem para empresa: ${codempresa}`);
      
      // Buscar modelo da empresa com n8n = true
      const { data, error } = await supabase
        .from('modelomensagem')
        .select('*')
        .eq('codempresa', codempresa)
        .eq('n8n', true)
        .limit(1);
        
      if (error) {
        console.error(`Erro ao buscar modelo para empresa ${codempresa}:`, error);
        return null;
      }
      
      if (!data || data.length === 0) {
        console.log(`Modelo não encontrado para empresa ${codempresa}`);
        return null;
      }
      
      console.log('Modelo encontrado para a empresa');
      return data[0].modelo; // Usando a coluna 'modelo' em vez de 'conteudo'
    } catch (err) {
      console.error('Erro ao buscar modelo de mensagem:', err);
      return null;
    }
  }
};
